import _isEqual from "lodash/isEqual";
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import UdeDataView from 'app/views/ude/UdeDataView';
import QueryModel from 'app/stores/ude/QueryModel';
import { Button, EmptyState, Flex } from 'core/components';
import { useDictionaryMeasurements } from 'app/stores/dictionary/useDictionaryMeasurements';
import $auth from 'app/stores/$auth';
import $ude from 'app/stores/ude/$ude';
import { addFilterGroup, mapFilters } from '@kentik/ui-shared/util/filters';
import { convertBoardVariablesToFilters } from 'app/stores/boards/convertBoardVariablesToFilters';
import { useGuidedValueResolver } from 'app/views/boards/guidedDimensionLookups';
import { ChartSyncContext } from 'app/views/boards/useChartSync';
import { usePanelEditContext } from 'app/views/boards/PanelEditContext';
import UdeDebugDialog from 'app/views/boards/panels/ude/debug/UdeDebugDialog';
import UdeEmptyState from './ai/UdeEmptyState';
import { addRowFilters } from './rowFilters';
import { useBoardPanelLoader } from './useBoardPanelLoader';
const VARIABLE_REF_PATTERN = /\{\{(.+?)}}/g;

/**
 * Replaces {{variableName}} references in existing filter values with the current variable value.
 * Supports partial substitution, e.g. `kt_{{foo}}` becomes `kt_<value>`.
 * Filters referencing an unresolved variable are removed entirely.
 */
function substituteVariableReferences(query, variables) {
  if (!query || !query.filters) {
    return query;
  }
  const variableMap = new Map(variables.map(v => [v.name, Array.isArray(v.value) ? v.value[0] ?? '' : v.value ?? '']));
  const substituteGroup = group => mapFilters(group, filter => {
    if (typeof filter.filterValue !== 'string' || !filter.filterValue.includes('{{')) {
      return filter; // Fast path: no template references
    }
    let hasUnresolved = false;
    const newValue = filter.filterValue.replace(VARIABLE_REF_PATTERN, (_, varName) => {
      const resolved = variableMap.get(varName);
      if (!resolved) {
        hasUnresolved = true;
      }
      return resolved ?? '';
    });
    return hasUnresolved ? null : {
      ...filter,
      filterValue: newValue
    };
  });
  return {
    ...query,
    filters: {
      ...query.filters,
      ...(query.filters?.dimensions ? {
        dimensions: substituteGroup(query.filters.dimensions)
      } : {}),
      ...(query.filters?.metrics ? {
        metrics: substituteGroup(query.filters.metrics)
      } : {})
    }
  };
}
function getQueryModelProps(config, panel) {
  return {
    query: {
      ...config.data,
      measurement: config.data.measurement || '/traffic',
      dimensions: config.data.dimensions || [],
      metrics: config.data.metrics || [],
      filters: {
        sources: config.data.filters?.sources || {
          connector: 'All',
          filterGroups: []
        },
        dimensions: config.data.filters?.dimensions || {
          connector: 'All',
          filterGroups: []
        },
        metrics: config.data.filters?.metrics || {
          connector: 'All',
          filterGroups: []
        }
      },
      time: config.data.time || {
        lookback: 3600
      },
      rollups: config.data.rollups || [],
      limit: config.data.limit ?? 100
    },
    settings: {
      chart: {
        ...config.chart,
        legend: {
          enable: config.chart?.legend?.enable ?? true,
          position: config.chart?.legend?.position ?? 'right',
          mode: config.chart?.legend?.mode ?? 'condensed',
          showRollups: config.chart?.legend?.showRollups ?? true,
          showEventsView: config.chart?.legend?.showEventsView ?? true
        },
        xAxis: {
          timezone: panel.board.get('config').timezone || config.chart?.xAxis?.timezone || $auth.userTimezone
        },
        yAxis: {
          type: config.chart?.yAxis?.type ?? 'linear',
          metric: config.chart?.yAxis?.metric || '',
          mirrorMode: config.chart?.yAxis?.mirrorMode ?? false,
          syncAxes: config.chart?.yAxis?.syncAxes ?? false
        },
        thresholds: config.chart?.thresholds ?? undefined
      }
    }
  };
}
function UdePanelBase({
  panel,
  config,
  form,
  onEditPanel,
  canCustomize,
  $syn
}) {
  const {
    query,
    settings
  } = getQueryModelProps(config, panel);
  const registerChart = useContext(ChartSyncContext);
  const panelEditContext = usePanelEditContext();
  // When the query changes, we want to re-run the measurement query with the new measurement name prefix filter (if applicable).
  // On fresh UDE panel, query will be undefined and we want to fetch all measurements, so we pass undefined for measurementName in that case.
  const {
    measurements
  } = useDictionaryMeasurements();

  // Consume a pending query stashed by "Open in UDE" navigation.
  // Only consume once form is available — consumePendingQuery() clears the stored value,
  // so calling it before form is ready would silently drop the handoff.
  useEffect(() => {
    if (!form) {
      return;
    }
    const pending = $ude.consumePendingQuery();
    if (pending) {
      const {
        query: pendingQuery,
        chart
      } = pending;

      // Bulk-set form fields from the pending query. setValuesDeep uses lodash get
      // to extract values by dot-path and falls back to current values for absent fields.
      form.setValuesDeep({
        config: {
          data: pendingQuery,
          chart: chart ?? {}
        }
      });

      // Push form state to panel model to trigger query execution
      const formConfig = form.getValues()?.config;
      if (formConfig) {
        panel.applyQueryConfig(formConfig);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // TODO: let's find a way to get rid of this indiscriminate collection fetching
  // Pre-fetch agents and tests so they're available for filters
  useEffect(() => {
    if ($syn?.agents && !$syn.agents.hasFetched) {
      $syn.agents.fetch();
    }
    if ($syn?.tests && !$syn.tests.hasFetched) {
      $syn.tests.fetch();
    }
  }, [$syn]);

  // Read board config outside useMemo so MobX observer tracks it.
  // When board.set('config', ...) is called, this re-renders and recomputes queryWithVariables.
  const boardConfig = panel.board.get('config');
  const boardVariables = React.useMemo(() => (boardConfig?.variables || []).filter(variable => variable.enabled !== false), [boardConfig?.variables]);

  // Board-level time/autorefresh overrides — read outside useMemo so MobX observer tracks them.
  const boardTimeRange = boardConfig?.time;
  const boardUpdateFrequency = boardConfig?.updateFrequency;

  // Panel-level mappings from config.variableMappings — stabilized with useMemo so the
  // reference only changes when the actual mapping data changes, not on every render.
  const variableMappings = React.useMemo(() => (config.variableMappings ?? []).reduce((acc, variableMapping) => {
    if (!variableMapping.disabled) {
      acc[variableMapping.variableName] = variableMapping;
    }
    return acc;
  }, {}), [config.variableMappings]);
  const resolveGuidedValue = useGuidedValueResolver();

  // Apply board variables to query before creating QueryModel
  const queryWithVariables = React.useMemo(() => {
    // Substitute any {{variableName}} references in existing filters first.
    const queryAfterSubstitution = substituteVariableReferences(query, boardVariables);
    if (!queryAfterSubstitution) {
      return query;
    }
    let queryAfterVariables;
    if (boardVariables.length === 0 || Object.keys(variableMappings).length === 0) {
      queryAfterVariables = queryAfterSubstitution;
    } else {
      // Add filters to query that are not being substituted, based on variable mappings. This allows variables to add filters to the query even if they're not directly referenced in existing filter values.
      const variableFilterGroup = convertBoardVariablesToFilters(boardVariables, variableMappings, resolveGuidedValue);
      if ((variableFilterGroup.filters?.length ?? 0) === 0 && (variableFilterGroup.filterGroups?.length ?? 0) === 0) {
        queryAfterVariables = queryAfterSubstitution;
      } else {
        const existingDimensions = queryAfterSubstitution.filters?.dimensions ?? {
          connector: 'All',
          filterGroups: []
        };
        const updatedDimensions = addFilterGroup(existingDimensions, variableFilterGroup, 'All');
        queryAfterVariables = {
          ...queryAfterSubstitution,
          filters: {
            ...(queryAfterSubstitution.filters ?? {}),
            dimensions: updatedDimensions
          }
        };
      }
    }

    // Apply board-level time range override when the user has changed the toolbar time.
    // This mirrors how board variables overlay filters on top of the panel's stored config,
    // without permanently modifying the saved panel query.
    let result = queryAfterVariables;
    if (boardTimeRange !== undefined) {
      result = {
        ...result,
        time: boardTimeRange
      };
    }

    // Apply board-level auto-refresh override when set.
    if (boardUpdateFrequency !== undefined) {
      result = {
        ...result,
        updateFrequency: boardUpdateFrequency
      };
    }
    return result;
  }, [query, boardVariables, variableMappings, boardTimeRange, boardUpdateFrequency, resolveGuidedValue]);
  const applicationMetadata = useMemo(() => ({
    data: {
      panel_id: String(panel.id)
    }
  }), [panel.id]);
  const [queryModel] = useState(() => panel.queryModel || new QueryModel(queryWithVariables, settings, {
    applicationMetadata
  }));

  // Keep queryTransformer and applicationMetadata in sync
  useEffect(() => {
    queryModel.applicationMetadata = applicationMetadata;
  }, [queryModel, applicationMetadata]);
  useEffect(() => {
    panel.queryModel = queryModel;
  }, [panel, queryModel]);
  const isInBoardView = !form;
  const {
    elementRef,
    isPending
  } = useBoardPanelLoader({
    panel,
    queryModel,
    isInBoardView
  });
  useEffect(() => {
    let runQuery = queryModel.forceRun;
    if (!_isEqual(queryModel.query, queryWithVariables)) {
      queryModel.query = queryWithVariables;
      queryModel.dirty = true;
      runQuery = true;
    }
    if (!_isEqual(queryModel.settings, settings)) {
      queryModel.settings = settings;
      queryModel.dirty = true;
    }
    if (runQuery && queryModel.hasRun) {
      queryModel.requestRun();
    }
  }, [queryModel, queryWithVariables, settings]);

  // Trigger query execution by pushing form state to panel model
  const handleRunQuery = useCallback(() => {
    if (form) {
      const formConfig = form.getValues()?.config;
      if (formConfig) {
        panel.applyQueryConfig(formConfig);
      }
    }
  }, [form, panel]);

  // Callback to update categoryPrefix state in the sidebar isn't needed here;
  // the empty state just needs to populate the form and run the query.
  const handleQueryApplied = useCallback(() => {
    // The empty-state AI input only needs to populate the form and trigger a query run.
  }, []);
  const {
    board
  } = panel;
  const handleAddRowFilters = useCallback((filters, {
    include
  }) => {
    const filterGroupsField = form?.getField('config.data.filters.dimensions.filterGroups');
    if (!filterGroupsField) {
      return;
    }
    const connectorField = form?.getField('config.data.filters.dimensions.connector');
    const current = {
      connector: connectorField?.getValue() ?? 'All',
      filterGroups: filterGroupsField.getValue() ?? []
    };
    const next = addRowFilters(current, filters, {
      include
    });
    filterGroupsField.setValue(next.filterGroups ?? []);
    connectorField?.setValue(next.connector);
    panelEditContext?.onRowFiltersAdded?.();
  }, [form, panelEditContext]);
  const handleZoomToSelection = useCallback((start, end) => {
    if (form) {
      form.setValue('config.data.time.lookback', 0);
      form.setValue('config.data.time.start', start);
      form.setValue('config.data.time.end', end);
      const formConfig = form.getValues()?.config;
      if (formConfig) {
        panel.applyQueryConfig(formConfig);
      }
    } else {
      board.setTimeRange({
        lookback: 0,
        start,
        end
      });
    }
  }, [board, form, panel]);
  if (!queryModel.query?.metrics?.length) {
    if (!form) {
      if (canCustomize) {
        return /*#__PURE__*/React.createElement(EmptyState, {
          action: /*#__PURE__*/React.createElement(Button, {
            icon: "edit",
            text: "Edit Panel",
            intent: "primary",
            outlined: true,
            onClick: () => onEditPanel(panel)
          })
        });
      }
      return null;
    }
    return /*#__PURE__*/React.createElement(UdeEmptyState, {
      form: form,
      measurements: measurements,
      onQueryApplied: handleQueryApplied,
      onRunQuery: handleRunQuery
    });
  }
  const dataView = /*#__PURE__*/React.createElement(UdeDataView, {
    queryModel: queryModel,
    onChartRef: registerChart?.(String(panel.id)),
    onZoomToSelection: handleZoomToSelection,
    onAddRowFilters: form ? handleAddRowFilters : undefined,
    isPending: isPending
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, isInBoardView ? /*#__PURE__*/React.createElement(Flex, {
    ref: elementRef,
    width: "100%",
    height: "100%",
    minHeight: 0,
    minWidth: 0
  }, dataView) : dataView, queryModel.showDebugDialog && /*#__PURE__*/React.createElement(UdeDebugDialog, {
    queryModel: queryModel,
    isOpen: true,
    onClose: () => queryModel.hideDebugDialog()
  }));
}
const UdePanel = inject('$syn')(observer(UdePanelBase));
export default UdePanel;